import * as fabric from 'fabric'
import { ArticoloType } from '@utils/types'
import { resolveEditorColorLimits } from '@utils/editor-bozze/colors'
import {
  CANVAS_CENTER_OFFSET,
  FABRIC_CANVAS_SIZE,
  PAN_PADDING,
  PREVIEW_OUTPUT_HEIGHT,
  PREVIEW_OUTPUT_WIDTH,
  PREVIEW_STRIP_WIDTH,
  PRINT_AREA_SIZE,
  SECONDARY_PREVIEW_MAX_SIZE,
  getAreaStampaImageUrl,
} from './fabric-editor-helpers'
import { drawImageContain, loadImageElement } from './fabric-editor-preview'
import { normalizeHexColor } from './index'

/**
 * Funzioni di export/preview separate da `useFabricEditor`.
 * Mantengono la logica di rendering PNG e composizione multi-colore in un modulo dedicato.
 */

export const getExportBoundsFromBackground = (background?: fabric.FabricImage | null) => {
  if (background) {
    return {
      left: background.left || 0,
      top: background.top || 0,
      width: background.getScaledWidth(),
      height: background.getScaledHeight(),
    }
  }

  return {
    left: 0,
    top: 0,
    width: PRINT_AREA_SIZE,
    height: PRINT_AREA_SIZE,
  }
}

export const getCanvasBoundsFromBackground = (background?: fabric.FabricImage | null) => {
  if (background) {
    const left = background.left || 0
    const top = background.top || 0
    const width = background.getScaledWidth()
    const height = background.getScaledHeight()

    return {
      left: left - PAN_PADDING,
      top: top - PAN_PADDING,
      right: left + width + PAN_PADDING,
      bottom: top + height + PAN_PADDING,
    }
  }

  return {
    left: 0,
    top: 0,
    right: FABRIC_CANVAS_SIZE,
    bottom: FABRIC_CANVAS_SIZE,
  }
}

export const getBackgroundOriginFromImage = (background?: fabric.FabricImage | null) => ({
  left: background?.left || CANVAS_CENTER_OFFSET,
  top: background?.top || CANVAS_CENTER_OFFSET,
})

export const getPrintAreaBoundsRelativeToBackground = (
  areaStampa: fabric.Object | null | undefined,
  background?: fabric.FabricImage | null
) => {
  if (!areaStampa) return null

  const backgroundOrigin = getBackgroundOriginFromImage(background)

  return {
    left: (areaStampa.left || 0) - backgroundOrigin.left,
    top: (areaStampa.top || 0) - backgroundOrigin.top,
    width: areaStampa.getScaledWidth(),
    height: areaStampa.getScaledHeight(),
  }
}

export const exportCanvasDataUrl = ({
  canvas,
  background,
  includeBackground = true,
  applyTranslucentFinish,
  toggleCanvasOverlayVisibility,
  isPreviewVisible,
}: {
  canvas: fabric.Canvas | null | undefined
  background: fabric.FabricImage | null | undefined
  includeBackground?: boolean
  applyTranslucentFinish: () => void
  toggleCanvasOverlayVisibility: (visible: boolean) => void
  isPreviewVisible: boolean
}) => {
  if (!canvas) return null

  applyTranslucentFinish()

  const exportBounds = getExportBoundsFromBackground(background)
  const previousViewport = canvas.viewportTransform
    ? ([...canvas.viewportTransform] as fabric.TMat2D)
    : null
  const previousBackgroundVisibility = background?.visible

  if (background && !includeBackground) {
    background.visible = false
  }

  toggleCanvasOverlayVisibility(false)
  canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
  canvas.renderAll()

  const dataURL = canvas.toDataURL({
    left: exportBounds.left,
    top: exportBounds.top,
    width: exportBounds.width,
    height: exportBounds.height,
    format: 'png',
    quality: includeBackground ? 1 : 0.5,
    multiplier: 1,
  })

  if (background && previousBackgroundVisibility !== undefined) {
    background.visible = previousBackgroundVisibility
  }

  if (previousViewport) {
    canvas.setViewportTransform(previousViewport)
    canvas.requestRenderAll()
  }

  toggleCanvasOverlayVisibility(!isPreviewVisible)

  return dataURL
}

export const buildPreviewByColorId = async ({
  overlayDataUrl,
  forcedSingleColorHex,
  articoli,
  personalizzazioneData,
}: {
  overlayDataUrl: string
  forcedSingleColorHex: string
  articoli: ArticoloType[]
  personalizzazioneData: any
}) => {
  if (!overlayDataUrl || !forcedSingleColorHex) {
    return {}
  }

  const articlePreviewColors = Array.from(
    new Map(
      (articoli || [])
        .map((articolo) => {
          const colorId = articolo?.colore?.id
          if (colorId === undefined || colorId === null) return null

          let colorHex = null
          const colorLimits = resolveEditorColorLimits(
            personalizzazioneData?.configurazione?.tecnica,
            articolo
          )
          if (colorLimits?.allowedColors?.length === 1) {
            colorHex = colorLimits.allowedColors?.[0]?.hex
          }
          if (!colorHex) return null

          return [String(colorId), colorHex] as const
        })
        .filter(Boolean)
    )
  )

  if (!articlePreviewColors.length) {
    return {}
  }

  const baseOverlayImage = await loadImageElement(overlayDataUrl)
  const tintedByHex = new Map<string, string>()
  const previewMap: Record<string, string> = {}

  for (const [colorId, colorHex] of articlePreviewColors) {
    if (colorHex === forcedSingleColorHex) {
      previewMap[colorId] = overlayDataUrl
      continue
    }

    if (!tintedByHex.has(colorHex)) {
      const tintCanvas = document.createElement('canvas')
      tintCanvas.width = baseOverlayImage.naturalWidth || baseOverlayImage.width
      tintCanvas.height = baseOverlayImage.naturalHeight || baseOverlayImage.height
      const tintCtx = tintCanvas.getContext('2d')
      if (!tintCtx) {
        tintedByHex.set(colorHex, overlayDataUrl)
      } else {
        tintCtx.drawImage(baseOverlayImage, 0, 0)
        tintCtx.globalCompositeOperation = 'source-in'
        tintCtx.fillStyle = colorHex
        tintCtx.fillRect(0, 0, tintCanvas.width, tintCanvas.height)
        tintCtx.globalCompositeOperation = 'source-over'
        tintedByHex.set(colorHex, tintCanvas.toDataURL('image/png'))
      }
    }

    previewMap[colorId] = tintedByHex.get(colorHex) || overlayDataUrl
  }

  return previewMap
}

export const getSecondaryPreviewItems = ({
  articoli,
  coloreId,
  personalizzazioneData,
}: {
  articoli: ArticoloType[]
  coloreId?: string | number | null
  personalizzazioneData: any
}) => {
  const otherArticoli = Array.from(
    new Map(
      (articoli || [])
        .filter((articolo) => articolo?.colore?.id !== coloreId)
        .map((articolo) => [articolo?.colore?.id, articolo])
    ).values()
  )

  return otherArticoli
    .map((articolo) => {
      const areaStampa = personalizzazioneData?.configurazione?.aree_stampa?.find(
        (area) => area.colore?.id === articolo?.colore?.id
      )
      const imageUrl = getAreaStampaImageUrl(areaStampa?.immagine_originale)

      if (!imageUrl) return null

      let colorHex = null
      const colorLimits = resolveEditorColorLimits(
        personalizzazioneData?.configurazione?.tecnica,
        articolo
      )
      if (colorLimits?.allowedColors?.length === 1) {
        colorHex = colorLimits.allowedColors?.[0]?.hex
      }

      return {
        articolo,
        imageUrl,
        colorHex,
      }
    })
    .filter(
      (item): item is { articolo: ArticoloType; imageUrl: string; colorHex: string } =>
        item !== null
    )
}

export const composeMultiColorPreview = async ({
  mainDataUrl,
  overlayDataUrl,
  secondaryItems,
  forcedSingleColorHex,
}: {
  mainDataUrl: string
  overlayDataUrl: string
  secondaryItems: Array<{ articolo: ArticoloType; imageUrl: string; colorHex: string }>
  forcedSingleColorHex: string
}) => {
  if (!secondaryItems.length) return mainDataUrl

  const [mainImage, overlayImage, ...secondaryBaseImages] = await Promise.all([
    loadImageElement(mainDataUrl),
    loadImageElement(overlayDataUrl),
    ...secondaryItems.map((item) => loadImageElement(item.imageUrl)),
  ])

  const outputCanvas = document.createElement('canvas')
  outputCanvas.width = PREVIEW_OUTPUT_WIDTH
  outputCanvas.height = PREVIEW_OUTPUT_HEIGHT

  const ctx = outputCanvas.getContext('2d')
  if (!ctx) return mainDataUrl

  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, PREVIEW_OUTPUT_WIDTH, PREVIEW_OUTPUT_HEIGHT)

  drawImageContain(ctx, mainImage, PREVIEW_STRIP_WIDTH, 0, PRINT_AREA_SIZE, PREVIEW_OUTPUT_HEIGHT)

  const columns = Math.max(1, Math.ceil(secondaryItems.length / 6))
  const rows = Math.max(1, Math.min(6, Math.ceil(secondaryItems.length / columns)))
  const cellSize = Math.min(
    PREVIEW_STRIP_WIDTH / columns,
    PREVIEW_OUTPUT_HEIGHT / rows,
    SECONDARY_PREVIEW_MAX_SIZE
  )
  const gridWidth = columns * cellSize
  const gridHeight = rows * cellSize
  const offsetX = (PREVIEW_STRIP_WIDTH - gridWidth) / 2
  const offsetY = (PREVIEW_OUTPUT_HEIGHT - gridHeight) / 2
  const tintedOverlayByHex = new Map<string, HTMLImageElement>()

  const getOverlayForItem = async (itemColorHex: string) => {
    const normalizedItemColor = normalizeHexColor(itemColorHex)
    if (!normalizedItemColor || normalizedItemColor === forcedSingleColorHex) {
      return overlayImage
    }

    if (tintedOverlayByHex.has(normalizedItemColor)) {
      return tintedOverlayByHex.get(normalizedItemColor)
    }

    const tintCanvas = document.createElement('canvas')
    tintCanvas.width = overlayImage.naturalWidth || overlayImage.width
    tintCanvas.height = overlayImage.naturalHeight || overlayImage.height
    const tintCtx = tintCanvas.getContext('2d')
    if (!tintCtx) return overlayImage

    tintCtx.drawImage(overlayImage, 0, 0)
    tintCtx.globalCompositeOperation = 'source-in'
    tintCtx.fillStyle = normalizedItemColor
    tintCtx.fillRect(0, 0, tintCanvas.width, tintCanvas.height)
    tintCtx.globalCompositeOperation = 'source-over'

    const tintedOverlay = await loadImageElement(tintCanvas.toDataURL('image/png'))
    tintedOverlayByHex.set(normalizedItemColor, tintedOverlay)

    return tintedOverlay
  }

  for (const [index, baseImage] of secondaryBaseImages.entries()) {
    const columnIndex = Math.floor(index / rows)
    const rowIndex = index % rows
    const cellX = offsetX + columnIndex * cellSize
    const cellY = offsetY + rowIndex * cellSize
    const drawBounds = drawImageContain(ctx, baseImage, cellX, cellY, cellSize, cellSize)
    const secondaryOverlay = await getOverlayForItem(secondaryItems[index]?.colorHex || '')

    ctx.drawImage(secondaryOverlay, drawBounds.x, drawBounds.y, drawBounds.width, drawBounds.height)
  }

  return outputCanvas.toDataURL('image/png')
}

export const getPreviewImage = async ({
  exportWithBackground,
  exportOverlayOnly,
  compose,
}: {
  exportWithBackground: () => string | null
  exportOverlayOnly: () => string | null
  compose: (mainDataUrl: string, overlayDataUrl: string) => Promise<string>
}) => {
  const mainDataUrl = exportWithBackground()
  if (!mainDataUrl) return null

  const overlayDataUrl = exportOverlayOnly()
  if (!overlayDataUrl) return mainDataUrl

  return compose(mainDataUrl, overlayDataUrl)
}

export const downloadPreviewDataUrl = (dataURL: string, filenamePrefix: string) => {
  const link = document.createElement('a')
  link.href = dataURL
  link.download = `bozza_${filenamePrefix}.png`

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
